// Rating a reply and reporting a problem (GID-83).
//
// 👍 / 👎 under every reply; 👎 asks why (reasons and an optional comment)
// before sending. "Report a problem" in the account menu takes a description.
// These only collect what the user chooses to send and hand it to `send`
// (chatAPI.sendFeedback): the backend stores it, scores the reply's Langfuse
// trace and, for 👎 and problems, tells the team through Sentry. Kept out of
// taskpane.js so the behaviour is unit-tested.

const REASONS = [
  ["wrong_result", "Wrong result"],
  ["not_what_i_asked", "Didn't do what I asked"],
  ["too_slow", "Too slow"],
  ["sync_problem", "Sync problem"],
  ["other", "Other"],
];

const THANKS = "Thanks for the feedback.";
const FAILED = "Couldn't send. Try again.";
const LIMITED = "Too many reports right now. Try again later.";
const REPORT_THANKS = "Thanks, the team has your report.";
const REPORT_CLOSE_MS = 2500;

function el(doc, tag, props = {}, children = []) {
  const node = doc.createElement(tag);
  Object.entries(props).forEach(([key, value]) => {
    if (key === "dataset") Object.assign(node.dataset, value);
    else if (key === "text") node.textContent = value;
    else if (key in node) node[key] = value;
    else node.setAttribute(key, value);
  });
  children.forEach((child) => node.appendChild(child));
  return node;
}

function idsOf({ sessionId, messageId, traceId }) {
  const ids = {};
  if (sessionId) ids.session_id = sessionId;
  if (messageId) ids.message_id = messageId;
  if (traceId) ids.langfuse_trace_id = traceId;
  return ids;
}

// The backend answers 200 with "rate_limited" when a user is over the hourly
// cap, so a resolved send is not the same as a recorded one.
const wasRecorded = (response) => !response || response.status !== "rate_limited";

function onEscape(node, close) {
  node.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      close();
    }
  });
}

/**
 * 👍 / 👎 for one reply. The pressed thumb is the rating the server holds:
 * opening 👎 is not a rating until it is sent, and a cancelled or failed
 * change leaves the recorded one in place.
 * @param {Document} doc
 * @param {{sessionId?: string, messageId?: string, traceId?: string, send: (body: object) => Promise}} options
 */
export function buildRatingControls(doc, { sessionId, messageId, traceId, send }) {
  const ids = idsOf({ sessionId, messageId, traceId });
  const status = el(doc, "span", { className: "feedback-status", role: "status" });
  const up = el(doc, "button", { type: "button", className: "feedback-thumb", title: "Good reply", text: "👍",
    dataset: { rating: "up" }, "aria-pressed": "false", "aria-label": "Good reply" });
  const down = el(doc, "button", { type: "button", className: "feedback-thumb", title: "Bad reply", text: "👎",
    dataset: { rating: "down" }, "aria-pressed": "false", "aria-label": "Bad reply", "aria-expanded": "false" });

  const chosen = new Set();
  const reasonButtons = REASONS.map(([key, label]) => {
    const chip = el(doc, "button", { type: "button", className: "feedback-reason", text: label,
      dataset: { reason: key }, "aria-pressed": "false" });
    chip.addEventListener("click", () => {
      if (chosen.has(key)) chosen.delete(key); else chosen.add(key);
      chip.setAttribute("aria-pressed", String(chosen.has(key)));
    });
    return chip;
  });
  const comment = el(doc, "textarea", { className: "feedback-comment", maxLength: 2000, rows: 2,
    placeholder: "What went wrong? (optional)", "aria-label": "What went wrong? (optional)" });
  const sendButton = el(doc, "button", { type: "button", className: "feedback-send", text: "Send" });
  const cancelButton = el(doc, "button", { type: "button", className: "feedback-cancel", text: "Cancel" });
  const panel = el(doc, "div", { className: "feedback-panel", hidden: true }, [
    el(doc, "div", { className: "feedback-reasons" }, reasonButtons),
    comment,
    el(doc, "div", { className: "feedback-actions" }, [sendButton, cancelButton]),
  ]);

  let recorded = null;
  const show = (rating) => {
    up.setAttribute("aria-pressed", String(rating === "up"));
    down.setAttribute("aria-pressed", String(rating === "down"));
  };
  const busy = (on) => {
    up.disabled = on;
    down.disabled = on;
  };
  const closePanel = () => {
    panel.hidden = true;
    down.setAttribute("aria-expanded", "false");
    down.focus?.();
  };

  const submit = async (rating, reasons, text) => {
    status.textContent = "";
    busy(true);
    try {
      const response = await send({ kind: "rating", rating, reasons, comment: text, ...ids });
      if (wasRecorded(response)) {
        recorded = rating;
        status.textContent = THANKS;
      } else {
        status.textContent = LIMITED;
      }
    } catch (_) {
      status.textContent = FAILED;
    } finally {
      busy(false);
      show(recorded);
    }
  };

  up.addEventListener("click", () => {
    if (!panel.hidden) closePanel();
    if (recorded === "up") return;
    submit("up", [], "");
  });
  down.addEventListener("click", () => {
    panel.hidden = false;
    down.setAttribute("aria-expanded", "true");
    comment.focus?.();
  });
  cancelButton.addEventListener("click", closePanel);
  onEscape(panel, closePanel);
  sendButton.addEventListener("click", () => {
    closePanel();
    submit("down", Array.from(chosen), comment.value.trim());
  });

  return el(doc, "div", { className: "feedback-controls" }, [up, down, status, panel]);
}

/**
 * The "Report a problem" form. It names the session and, when there is one,
 * the latest reply's Langfuse trace, so the team lands on the turn the user
 * was looking at. Closes itself a moment after a successful send.
 * @param {Document} doc
 * @param {{getSessionId: () => (string|null), getTraceId?: () => (string|null),
 *   send: (body: object) => Promise, onClose: () => void}} options
 */
export function buildProblemReport(doc, { getSessionId, getTraceId = () => null, send, onClose }) {
  const status = el(doc, "div", { className: "feedback-status", role: "status" });
  const text = el(doc, "textarea", { className: "feedback-comment", maxLength: 2000, rows: 5,
    placeholder: "What happened, and what did you expect?", "aria-label": "Describe the problem" });
  const sendButton = el(doc, "button", { type: "button", className: "feedback-send", text: "Send report" });
  const cancelButton = el(doc, "button", { type: "button", className: "feedback-cancel", text: "Cancel" });

  const deliver = async (body) => {
    try {
      return await send(body);
    } catch (error) {
      // The chat was deleted since: the report still matters without it.
      if (error?.status === 404 && body.session_id) {
        const { session_id: _gone, langfuse_trace_id: _trace, ...rest } = body;
        return send(rest);
      }
      throw error;
    }
  };

  sendButton.addEventListener("click", async () => {
    const comment = text.value.trim();
    if (!comment) {
      status.textContent = "Please describe the problem.";
      return;
    }
    const body = { kind: "problem", comment };
    const sessionId = getSessionId();
    if (sessionId) body.session_id = sessionId;
    const traceId = getTraceId();
    if (traceId) body.langfuse_trace_id = traceId;
    sendButton.disabled = true;
    try {
      const response = await deliver(body);
      if (wasRecorded(response)) {
        status.textContent = REPORT_THANKS;
        text.value = "";
        setTimeout(() => onClose(), REPORT_CLOSE_MS);
      } else {
        status.textContent = LIMITED;
      }
    } catch (_) {
      status.textContent = FAILED;
    } finally {
      sendButton.disabled = false;
    }
  });
  cancelButton.addEventListener("click", () => onClose());

  const form = el(doc, "div", { className: "problem-report", role: "dialog", "aria-label": "Report a problem" }, [
    el(doc, "div", { className: "problem-report-title", text: "Report a problem" }),
    text,
    el(doc, "div", { className: "feedback-actions" }, [sendButton, cancelButton]),
    status,
  ]);
  onEscape(form, () => onClose());
  return form;
}

/**
 * Opens the report form above the chat (one at a time), closing the account
 * menu first: the menu is a popover over the header and would cover it.
 * @param {Document} doc
 * @param {{getSessionId: () => (string|null), getTraceId?: () => (string|null),
 *   send: (body: object) => Promise}} options
 * @returns {HTMLElement} the form
 */
export function mountProblemReport(doc, { getSessionId, getTraceId, send }) {
  const existing = doc.getElementById("problemReport");
  if (existing) {
    existing.querySelector("textarea")?.focus();
    return existing;
  }
  const panel = doc.getElementById("accountPanel");
  if (panel) panel.hidden = true;
  const form = buildProblemReport(doc, {
    getSessionId,
    getTraceId,
    send,
    onClose: () => {
      const open = doc.getElementById("problemReport");
      if (!open) return;
      open.remove();
      doc.getElementById("accountButton")?.focus?.();
    },
  });
  form.id = "problemReport";
  const messages = doc.getElementById("chatMessages");
  if (messages?.parentNode) messages.parentNode.insertBefore(form, messages);
  else doc.body.appendChild(form);
  form.scrollIntoView?.({ block: "nearest" });
  form.querySelector("textarea")?.focus();
  return form;
}
